/* eslint-disable max-lines */
import { EventBus, Events, store } from "jiffy-store";
import debounce from "lodash.debounce";
import { pickStoreCartSum } from "common/utils/cart/pick-store-cart-sum";
import { formatCurrency } from "common/utils/format/number";
import idleCallback from "common/utils/ui/idle_callback";

const SLIDER_CONFIG = {
  MIN_QTY: 1,
  MAX_QTY: 300,
  MID_QTY: 100,
  MID_POSITION: 0.5
};

const COMPONENT_MODES = {
  PDP: "pdp",
  CARD: "card"
};

// Flexible variant ID retrieval that works for both PDP and card contexts
const getVariantId = element => {
  // First check for data attribute (product card mode)
  if (element?.dataset?.variantId) {
    return element.dataset.variantId;
  }

  // Fall back to DOM selector method (PDP mode)
  const base = ".js-product-details-quantity-form-row-item";
  const $preferred = document.querySelector(`${base}[data-size='M']`);
  const $fallback = $preferred || document.querySelector(`${base}[data-variant-id]`);
  return $fallback?.dataset?.variantId || null;
};

// PDP-only: resolve a hovered composition to a variant ID without changing selection
// Prefer using the swatch's own dataset (works even when only selected color's grid is rendered).
// Fall back to quantity-grid inputs for safety.
const getVariantIdForCompositionId = compositionId => {
  if (!compositionId) return null;

  const $paletteItem = document.querySelector(
    `.js-product-color-palette-color-item[data-composition-id="${compositionId}"]`
  );
  if ($paletteItem?.dataset?.variantId) return $paletteItem.dataset.variantId;

  const base = `.js-quantity-form-input[data-composition-id="${compositionId}"]`;
  const $preferred = document.querySelector(`${base}[data-size="M"]`);
  const $fallback = $preferred || document.querySelector(`${base}[data-variant-id]`);
  return $fallback?.dataset?.variantId || null;
};

const getCartSum = () => {
  // First try to get from store
  const storeValue = store.get(state => pickStoreCartSum(state.pdp));

  if (storeValue !== null && storeValue !== undefined && !Number.isNaN(storeValue)) {
    return storeValue;
  }

  // Try document.pdpDynamicData (for PDP pages)
  if (typeof document !== "undefined" && document.pdpDynamicData?.cart_total) {
    return parseFloat(document.pdpDynamicData.cart_total);
  }

  // Try to get from cart element in DOM
  const cartElement = document.querySelector("[data-cart-total]");
  if (cartElement?.dataset?.cartTotal) {
    return parseFloat(cartElement.dataset.cartTotal);
  }

  return 0;
};

// Cache for product pricing data to avoid duplicate API calls
const pricingCache = new Map();
const pricingPromises = new Map();

// Fetch pricing data from CP endpoint (for card mode)
const fetchCustomPricing = async productId => {
  // Check if we have cached data
  if (pricingCache.has(productId)) {
    return pricingCache.get(productId);
  }

  // Check if a fetch is already in progress for this product
  if (pricingPromises.has(productId)) {
    return pricingPromises.get(productId);
  }

  // Create a new promise for this product
  const promise = (async () => {
    try {
      const url = `/api/products/${productId}/cp?made_dedicated_pdp=true&made=true&print=print_default`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Failed to fetch pricing: ${response.status}`);
      const data = await response.json();

      // Cache the successful result
      pricingCache.set(productId, data);

      // Clear the promise after caching
      pricingPromises.delete(productId);

      return data;
    } catch (error) {
      console.error("Error fetching custom pricing:", error);

      // Clear the promise on error so it can be retried
      pricingPromises.delete(productId);

      return null;
    }
  })();

  // Store the promise so other calls can wait for it
  pricingPromises.set(productId, promise);

  return promise;
};

const convertNonResellerVariantPrices = pricingData => {
  if (!pricingData?.non_reseller_variant_prices) return null;

  const convertedNonResellerPrices = {};
  Object.entries(pricingData.non_reseller_variant_prices).forEach(([variantId, prices]) => {
    convertedNonResellerPrices[variantId] = {};
    Object.entries(prices).forEach(([qty, price]) => {
      convertedNonResellerPrices[variantId][qty] = parseFloat(price);
    });
  });

  return convertedNonResellerPrices;
};

class BulkDiscountSlider {
  minQuantity = SLIDER_CONFIG.MIN_QTY;

  maxQuantity = SLIDER_CONFIG.MAX_QTY;

  midQuantity = SLIDER_CONFIG.MID_QTY;

  midPosition = SLIDER_CONFIG.MID_POSITION;

  quantity = 25;

  loadedPrices = false;

  isDisabled = true;

  variantPrices = {};

  nonResellerVariantPrices = null;

  mode = COMPONENT_MODES.PDP;

  variantIdBeforePreview = null;

  get originalPrice() {
    // For resellers, show regular (non-reseller) price at qty 1 as the slashed price
    // This matches the PLP popup's Regular Price column at qty 1
    const nonResellerPrices = this.nonResellerVariantPrices?.[this.variantId];
    if (nonResellerPrices) {
      // Calculate tier based on cart value + price for 1 item (matching PLP behavior)
      const tierZeroPrice = nonResellerPrices[0] ?? 0;
      const totalAmount = getCartSum() + tierZeroPrice;

      const tiers = Object.keys(nonResellerPrices)
        .map(tier => parseFloat(tier))
        .filter(tier => !Number.isNaN(tier))
        .sort((a, b) => a - b);

      const currentTier = [...tiers].reverse().find(tierThreshold => tierThreshold <= totalAmount);
      const price = nonResellerPrices[currentTier] ?? nonResellerPrices[String(currentTier)];
      return price ?? tierZeroPrice;
    }

    // Fallback for non-resellers: use own variant prices at tier 0
    const variantPrices = this.variantPrices[this.variantId];
    if (!variantPrices) return 0;
    return variantPrices[0] || 0;
  }

  get totalOriginalPrice() {
    return this.originalPrice * this.quantity;
  }

  get totalDiscountedPrice() {
    return this.discountedPrice * this.quantity;
  }

  get discountedPrice() {
    const variantPrices = this.variantPrices[this.variantId];
    if (!variantPrices) return 0;

    // Calculate tier using user's own tier 0 price × quantity
    // For resellers: uses wholesale tier 0; for non-resellers: uses regular tier 0
    const tierZeroPrice = variantPrices[0] ?? variantPrices["0"] ?? 0;
    const totalAmount = getCartSum() + Number(tierZeroPrice) * this.quantity;

    const tiers = Object.keys(variantPrices)
      .map(tier => parseFloat(tier))
      .filter(tier => !Number.isNaN(tier))
      .sort((a, b) => a - b);

    // Use reverse() and find() for better browser compatibility
    const currentTier = [...tiers].reverse().find(tierThreshold => tierThreshold <= totalAmount);
    const price = variantPrices[currentTier] ?? variantPrices[String(currentTier)];

    return price ?? variantPrices[0] ?? 0;
  }

  get savedPercentage() {
    const base = this.totalOriginalPrice;
    if (!Number.isFinite(base) || base <= 0) return 0;
    const pct = ((base - this.totalDiscountedPrice) / base) * 100;
    return Math.max(0, Math.min(100, pct));
  }

  getNextDiscountTier() {
    const variantPrices = this.variantPrices[this.variantId];
    if (!variantPrices) return null;

    const currentTotal = getCartSum() + this.totalOriginalPrice;

    // Find the next tier threshold
    const sortedTiers = Object.keys(variantPrices)
      .map(tier => parseFloat(tier))
      .sort((a, b) => a - b);

    const nextTier = sortedTiers.find(tier => tier > currentTotal);

    if (!nextTier) return null;

    // Calculate how much more is needed to reach the next tier
    const amountNeeded = nextTier - currentTotal;
    const nextTierPrice = variantPrices[nextTier];
    const currentPrice = this.discountedPrice;
    const additionalSavings = (((currentPrice - nextTierPrice) / currentPrice) * 100).toFixed(0);

    return {
      threshold: nextTier,
      amountNeeded,
      price: nextTierPrice,
      additionalSavings
    };
  }

  constructor(element) {
    this.$slider = element;
    if (!this.$slider) return;

    // Store instance reference on element for updates
    this.$slider.sliderInstance = this;

    // Determine mode from data attribute
    this.mode = this.$slider.dataset.mode || COMPONENT_MODES.PDP;

    // Get data attributes for card mode
    if (this.mode === COMPONENT_MODES.CARD) {
      this.productId = this.$slider.dataset.productId;
    }

    // Get variant ID using flexible method
    this.variantId = getVariantId(this.$slider);
    // Default quantity is always 25 for both modes
    this.quantity = 25;

    // Cache DOM elements
    this.cacheDOMElements();

    // Bind methods
    this.boundHandleSliderMove = this.handleSliderMove.bind(this);
    this.boundHandleSliderEnd = this.handleSliderEnd.bind(this);
    this.boundHandleResize = this.handleResize.bind(this);
    this.boundHandleInputChange = this.handleInputChange.bind(this);
    this.debouncedUpdatePrices = debounce(this.updatePrices.bind(this), 150);

    this.init();
  }

  cacheDOMElements() {
    this.$sliderHandle = this.$slider.querySelector(".js-bulk-slider-handle");
    this.$sliderProgress = this.$slider.querySelector(".js-bulk-slider-progress");
    this.$sliderQuantityInput = this.$slider.querySelector(".js-bulk-slider-quantity-input");
    this.$sliderDiscountedPriceWrapper = this.$slider.querySelector(
      ".js-bulk-slider-discounted-price-wrapper"
    );
    this.$sliderDiscountedPrice = this.$slider.querySelector(".js-bulk-slider-discounted-price");
    this.$sliderOriginalPrice = this.$slider.querySelector(".js-bulk-slider-original-price");
    this.$sliderTotalPrice = this.$slider.querySelector(".js-bulk-slider-total-price");
    this.$sliderTotalItems = this.$slider.querySelector(".js-bulk-slider-total-items");
    this.$sliderSavedPercentage = this.$slider.querySelector(".js-bulk-slider-saved-percentage");
    this.$savedBadge = this.$slider.querySelector(".js-saved-badge");
    this.$wholesalePerItemSavedText = this.$slider.querySelector(
      ".js-wholesale-per-item-saved-text"
    );
    this.$wholesalePerItemSavedSkeleton = this.$slider.querySelector(
      ".js-wholesale-per-item-saved-skeleton"
    );
    this.$priceSkeletons = this.$slider.querySelectorAll(".js-price-skeleton");
    this.$sliderMessage = this.$slider.querySelector(".js-slider-message");
    this.$sliderItemsLabels = this.$slider.querySelectorAll(".js-bulk-slider-items-label");
  }

  init() {
    if (this.mode === COMPONENT_MODES.PDP) {
      this.attachPriceLoadListener();
      this.attachMobileGridListener();
      this.attachColorPreviewListeners();
    } else {
      // Card mode: fetch pricing data
      this.fetchPricingData();
    }
    this.initSlider();

    // Update prices if cart data is already available
    const cartTotal = getCartSum();
    if (cartTotal > 0) {
      this.updatePrices();
    }
  }

  attachPriceLoadListener() {
    store.pdp.changed((_, { data }) => {
      if (data.loadedPrices && !this.loadedPrices) {
        this.loadedPrices = true;
        this.variantPrices = data.loadedPrices.variant_prices;
        this.nonResellerVariantPrices = data.loadedPrices.non_reseller_variant_prices || null;
        this.setDisabledState(false);
        this.updateSliderVisual();
        this.updatePrices();
      }
    });
  }

  attachMobileGridListener() {
    EventBus.on(Events.MOBILE_GRID_LOADED, () => {
      this.variantId = getVariantId(this.$slider);
      this.updateSliderVisual();
      this.updatePrices();
    });
  }

  attachColorPreviewListeners() {
    EventBus.on(Events.PRODUCT_COLOR_PREVIEW, (_event, { compositionId }) => {
      if (this.mode !== COMPONENT_MODES.PDP) return;
      if (!this.loadedPrices || this.isDisabled) return;

      const previewVariantId = getVariantIdForCompositionId(compositionId);
      if (!previewVariantId) return;

      if (!this.variantIdBeforePreview) {
        this.variantIdBeforePreview = this.variantId || getVariantId(this.$slider);
      }

      if (previewVariantId === this.variantId) return;
      this.variantId = previewVariantId;
      this.updatePrices();
    });

    EventBus.on(Events.PRODUCT_COLOR_PREVIEW_END, () => {
      if (this.mode !== COMPONENT_MODES.PDP) return;
      if (!this.loadedPrices || this.isDisabled) return;

      const currentSelectedVariantId = getVariantId(this.$slider);
      this.variantId = currentSelectedVariantId || this.variantIdBeforePreview || this.variantId;
      this.variantIdBeforePreview = null;
      this.updatePrices();
    });
  }

  async fetchPricingData() {
    if (!this.productId) {
      console.error("No product ID available for fetching pricing");
      return;
    }

    const pricingData = await fetchCustomPricing(this.productId);
    if (pricingData && pricingData.variant_prices) {
      this.loadedPrices = true;

      // Convert all variant prices from strings to numbers
      const convertedPrices = {};
      Object.entries(pricingData.variant_prices).forEach(([variantId, prices]) => {
        convertedPrices[variantId] = {};
        Object.entries(prices).forEach(([qty, price]) => {
          convertedPrices[variantId][qty] = parseFloat(price);
        });
      });

      this.variantPrices = convertedPrices;
      this.nonResellerVariantPrices = convertNonResellerVariantPrices(pricingData);

      // For card mode without explicit variant ID, use first available
      if (!this.variantId && Object.keys(convertedPrices).length > 0) {
        [this.variantId] = Object.keys(convertedPrices);
      }

      this.setDisabledState(false);
      this.updateSliderVisual();
      this.updatePrices();
    } else {
      console.error("Failed to load pricing data");
      this.showError();
    }
  }

  showError() {
    // Hide price skeletons and show error message
    this.$priceSkeletons.forEach($skeleton => {
      $skeleton.style.display = "none";
    });
    this.$sliderMessage.textContent = "Unable to load pricing";
    this.$sliderMessage.classList.add("text-red-600");
  }

  setValues() {
    this.updateSliderVisual();
    this.debouncedUpdatePrices();
  }

  updateSliderVisual() {
    this.$sliderQuantityInput.value = this.quantity;
    this.$sliderTotalItems.textContent = this.quantity;
    this.$sliderItemsLabels.forEach(label => {
      // eslint-disable-next-line no-param-reassign
      label.textContent = this.quantity === 1 ? "item" : "items";
    });
    this.updateSliderPosition();
  }

  updatePrices() {
    requestAnimationFrame(() => {
      // Remove skeleton loaders if they exist
      this.$priceSkeletons?.forEach(skeleton => skeleton.remove());

      // Update price values
      if (this.$sliderOriginalPrice) {
        this.$sliderOriginalPrice.textContent = formatCurrency(this.originalPrice);
      }
      if (this.$sliderDiscountedPrice) {
        this.$sliderDiscountedPrice.textContent = formatCurrency(this.discountedPrice);
      }
      if (this.$sliderTotalPrice) {
        this.$sliderTotalPrice.textContent = formatCurrency(this.totalDiscountedPrice);
      }
      if (this.$sliderSavedPercentage) {
        this.$sliderSavedPercentage.textContent = `${this.savedPercentage.toFixed(0)}%`;
      }
      this.renderWholesalePerItemSaved();

      // Handle saved badge - only show if there's actual savings
      if (this.savedPercentage > 0) {
        this.$savedBadge?.classList.remove("opacity-0");
      } else {
        this.$savedBadge?.classList.add("opacity-0");
      }
      this.$sliderDiscountedPriceWrapper?.classList.remove("animate-pulse");

      // Update slider message based on mode and next discount tier
      if (this.$sliderMessage && !this.isDisabled) {
        if (this.mode === COMPONENT_MODES.CARD) {
          this.updateCardModeMessage();
        } else {
          this.$sliderMessage.textContent = "Slide for bigger savings!";
          this.$sliderMessage.classList.add("text-green-700");
          this.$sliderMessage.classList.remove("text-gray-500");
        }
      }
    });
  }

  renderWholesalePerItemSaved() {
    if (!this.$wholesalePerItemSavedText) return;

    const saved = this.getWholesalePerItemSavedAmount();
    if (saved === null) {
      this.$wholesalePerItemSavedText.classList.add("hidden");
      this.$wholesalePerItemSavedSkeleton?.classList.remove("hidden");
      return;
    }

    this.$wholesalePerItemSavedText.textContent = `${formatCurrency(saved)} per item saved with Wholesale Pricing`;
    this.$wholesalePerItemSavedText.classList.remove("hidden");
    this.$wholesalePerItemSavedSkeleton?.classList.add("hidden");
  }

  getWholesalePerItemSavedAmount() {
    if (!this.loadedPrices || this.isDisabled) return null;

    // Only show savings if we have non-reseller prices to compare
    const hasNonResellerPrices = this.nonResellerVariantPrices?.[this.variantId];
    if (!hasNonResellerPrices) return null;

    // Use originalPrice (non-reseller) and discountedPrice (reseller) for consistency
    // Convert to numbers since backend may return strings (BigDecimal serialization)
    const nonReseller = Number(this.originalPrice);
    const offered = Number(this.discountedPrice);

    // Validate prices and ensure there's actual savings
    if (!Number.isFinite(nonReseller) || !Number.isFinite(offered)) return null;
    if (nonReseller <= 0 || offered <= 0) return null;

    const savings = nonReseller - offered;
    return savings > 0 ? savings : null;
  }

  updateCardModeMessage() {
    const nextTier = this.getNextDiscountTier();

    if (nextTier) {
      // this.$sliderMessage.textContent = `Add ${formatCurrency(nextTier.amountNeeded)} more to save ${nextTier.additionalSavings}`;
      this.$sliderMessage.textContent = "Slide for bigger savings!";
      this.$sliderMessage.classList.add("text-green-700");
      this.$sliderMessage.classList.remove("text-gray-500", "font-bold");
    } else {
      // this.$sliderMessage.textContent = "Maximum discount reached";
      this.$sliderMessage.textContent = "Slide for bigger savings!";
      this.$sliderMessage.classList.add("text-green-700", "font-bold");
      this.$sliderMessage.classList.remove("text-gray-500");
    }
  }

  initSlider() {
    if (!this.$sliderHandle || !this.$sliderProgress || !this.$sliderQuantityInput) return;

    this.$sliderTrack = this.$sliderProgress.parentElement;
    this.cacheDimensions();
    this.updateSliderPosition();
    this.updateInputConstraints();
    this.setDisabledState(true);

    this.$sliderHandle.addEventListener("mousedown", this.handleSliderStart.bind(this));
    // Not passive: we call preventDefault() to stop scrolling while dragging
    this.$sliderHandle.addEventListener("touchstart", this.handleSliderStart.bind(this), {
      passive: false
    });
    this.$sliderTrack.addEventListener("click", this.handleTrackClick.bind(this));
    this.$sliderQuantityInput.addEventListener("input", this.boundHandleInputChange);
    this.$sliderQuantityInput.addEventListener("blur", this.boundHandleInputChange);
    window.addEventListener("resize", this.boundHandleResize);
  }

  updateInputConstraints() {
    this.$sliderQuantityInput.min = this.minQuantity;
    this.$sliderQuantityInput.max = this.maxQuantity;
    this.$sliderQuantityInput.step = 1;
  }

  setDisabledState(disabled) {
    this.isDisabled = disabled;

    if (this.$sliderQuantityInput) {
      this.$sliderQuantityInput.disabled = disabled;
    }

    this.$sliderHandle?.classList?.toggle("border-gray-400", disabled);
    this.$sliderHandle?.classList?.toggle("border-green-700", !disabled);
    this.$sliderHandle?.classList?.toggle("pointer-events-none", disabled);
    this.$sliderHandle?.classList?.toggle("hover:scale-110", !disabled);
    this.$sliderTrack?.classList?.toggle("opacity-50", disabled);
    this.$sliderProgress?.classList?.toggle("bg-gray-400", disabled);
    this.$sliderProgress?.classList?.toggle("bg-green-700", !disabled);

    // Update message
    if (this.$sliderMessage) {
      this.$sliderMessage.textContent = disabled
        ? "Loading prices..."
        : "Slide for bigger savings!";
      this.$sliderMessage.classList.toggle("text-gray-500", disabled);
      this.$sliderMessage.classList.toggle("text-green-700", !disabled);
    }
  }

  cacheDimensions() {
    this.trackWidth = this.$sliderTrack.offsetWidth;
    this.handleWidth = this.$sliderHandle.offsetWidth;
    this.maxHandlePosition = this.trackWidth - this.handleWidth;
  }

  positionToQuantity(normalizedPosition) {
    if (normalizedPosition <= this.midPosition) {
      const ratio = normalizedPosition / this.midPosition;
      return Math.round(this.minQuantity + (this.midQuantity - this.minQuantity) * ratio);
    }
    const ratio = (normalizedPosition - this.midPosition) / (1 - this.midPosition);
    return Math.round(this.midQuantity + (this.maxQuantity - this.midQuantity) * ratio);
  }

  quantityToPosition(quantity) {
    if (quantity <= this.midQuantity) {
      const ratio = (quantity - this.minQuantity) / (this.midQuantity - this.minQuantity);
      return ratio * this.midPosition;
    }
    const ratio = (quantity - this.midQuantity) / (this.maxQuantity - this.midQuantity);
    return this.midPosition + ratio * (1 - this.midPosition);
  }

  handleResize() {
    idleCallback(() => {
      this.cacheDimensions();
      this.updateSliderPosition();
    });
  }

  handleSliderStart(e) {
    if (this.isDisabled) return;

    e.preventDefault();
    this.isDragging = true;
    this.$sliderHandle.classList.add("active");
    this.cacheDimensions();

    const clientX = e.type === "mousedown" ? e.clientX : e.touches[0].clientX;
    const handleRect = this.$sliderHandle.getBoundingClientRect();
    this.dragOffset = clientX - handleRect.left - this.handleWidth / 2;

    if (e.type === "mousedown") {
      document.addEventListener("mousemove", this.boundHandleSliderMove);
      document.addEventListener("mouseup", this.boundHandleSliderEnd);
    } else {
      document.addEventListener("touchmove", this.boundHandleSliderMove, { passive: true });
      document.addEventListener("touchend", this.boundHandleSliderEnd);
    }
  }

  handleSliderMove(e) {
    if (!this.isDragging) return;

    const clientX = e.type === "mousemove" ? e.clientX : e.touches[0].clientX;
    const trackRect = this.$sliderTrack.getBoundingClientRect();

    let position = clientX - trackRect.left - this.dragOffset;
    position = Math.max(0, Math.min(position, this.maxHandlePosition));

    const normalizedPosition = position / this.maxHandlePosition;
    const newQuantity = this.positionToQuantity(normalizedPosition);

    if (newQuantity !== this.quantity) {
      this.quantity = newQuantity;
      this.setValues();
    }
  }

  handleSliderEnd() {
    this.isDragging = false;
    this.$sliderHandle.classList.remove("active");

    document.removeEventListener("mousemove", this.boundHandleSliderMove);
    document.removeEventListener("mouseup", this.boundHandleSliderEnd);
    document.removeEventListener("touchmove", this.boundHandleSliderMove);
    document.removeEventListener("touchend", this.boundHandleSliderEnd);

    this.debouncedUpdatePrices.flush();
  }

  handleTrackClick(e) {
    if (this.isDisabled || e.target === this.$sliderHandle) return;

    this.cacheDimensions();
    const trackRect = this.$sliderTrack.getBoundingClientRect();

    let position = e.clientX - trackRect.left - this.handleWidth / 2;
    position = Math.max(0, Math.min(position, this.maxHandlePosition));

    const normalizedPosition = position / this.maxHandlePosition;
    this.quantity = this.positionToQuantity(normalizedPosition);
    this.setValues();
  }

  handleInputChange(e) {
    let value = parseInt(e.target.value, 10);

    value = Number.isNaN(value)
      ? this.minQuantity
      : Math.max(this.minQuantity, Math.min(value, this.maxQuantity));

    if (value !== this.quantity) {
      this.quantity = value;
      this.setValues();
    } else if (e.type === "blur") {
      this.$sliderQuantityInput.value = this.quantity;
    }
  }

  updateSliderPosition() {
    if (!this.$sliderTrack || !this.$sliderHandle || !this.$sliderProgress || !this.trackWidth)
      return;

    const normalizedPosition = this.quantityToPosition(this.quantity);
    const position = normalizedPosition * this.maxHandlePosition;
    const progressWidth = position + this.handleWidth / 2;

    this.$sliderHandle.style.left = `${position}px`;
    this.$sliderProgress.style.width = `${progressWidth}px`;
  }

  destroy() {
    window.removeEventListener("resize", this.boundHandleResize);
    if (this.$sliderQuantityInput) {
      this.$sliderQuantityInput.removeEventListener("input", this.boundHandleInputChange);
      this.$sliderQuantityInput.removeEventListener("blur", this.boundHandleInputChange);
    }
    this.debouncedUpdatePrices.cancel();

    // Clean up instance reference
    if (this.$slider) {
      delete this.$slider.sliderInstance;
    }
  }
}

// Singleton instance for PDP mode
let pdpInstance = null;

// Initialize for PDP mode (singleton)
const initPDP = () => {
  if (pdpInstance) return pdpInstance;
  const element = document.querySelector(".js-bulk-discount-slider[data-mode='pdp']");
  if (element) {
    pdpInstance = new BulkDiscountSlider(element);
  }
  return pdpInstance;
};

// Initialize all sliders on the page (for card mode)
function initializeSliders() {
  const sliders = document.querySelectorAll(".js-bulk-discount-slider[data-mode='card']");
  sliders.forEach(slider => {
    if (!slider.dataset.initialized) {
      // eslint-disable-next-line no-new
      new BulkDiscountSlider(slider);
      slider.dataset.initialized = "true";
    }
  });
}

// Helper function to update all sliders
function updateAllSliders() {
  const sliders = document.querySelectorAll(".js-bulk-discount-slider[data-initialized='true']");
  sliders.forEach(slider => {
    // If slider instance exists, update its prices
    if (slider.sliderInstance) {
      slider.sliderInstance.updatePrices();
    }
  });
}

// Initialize sliders with cart data (for card mode)
function initializeSlidersWithCart() {
  initializeSliders();
}

// Auto-initialize based on mode
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    // Check if we're on PDP
    if (document.querySelector(".js-bulk-discount-slider[data-mode='pdp']")) {
      initPDP();
    }
    // Initialize card mode sliders
    initializeSlidersWithCart();
  });
} else {
  // Check if we're on PDP
  if (document.querySelector(".js-bulk-discount-slider[data-mode='pdp']")) {
    initPDP();
  }
  // Initialize card mode sliders
  initializeSlidersWithCart();
}

// Listen for cart updates and store the data
EventBus.on(Events.SAVED_CARTS_LOADED, (_, data) => {
  if (data && data.carts && data.activeCart) {
    const { carts, activeCart } = data;

    // Store in pdp state for bulk discount slider to use
    store.pdp.change({
      savedCarts: carts,
      activeCart,
      originalCartTotal: Number(activeCart.amount) || 0
    });
  }

  updateAllSliders();
});

// Listen for items added to cart
EventBus.on(Events.ITEMS_ADDED_TO_CART, ({ target }) => {
  // Update the store with the new cart total
  if (target && target.cart_total !== undefined) {
    const activeCart = store.get(state => state.pdp.activeCart);
    if (activeCart) {
      activeCart.amount = target.cart_total;
      store.pdp.change({ activeCart });
    } else {
      // If no active cart, update originalCartTotal
      store.pdp.change({ originalCartTotal: parseFloat(target.cart_total) });
    }
  }
  updateAllSliders();
});

// Listen for dynamic content updates (for card mode)
document.addEventListener("products:loaded", initializeSliders);
document.addEventListener("plp:updated", initializeSliders);

export { initPDP, BulkDiscountSlider };
export default initPDP;
